import { useState, useEffect, useRef, useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useIntent } from "@/context/IntentContext";

// Viewport and assets
const FRONT_CURTAIN_LEFT = "/frontassets/front_curtain_left.jpg";
const FRONT_CURTAIN_RIGHT = "/frontassets/front_curtain_right.jpg";
const FRONT_CURTAINS_LOOP = "/frontassets/front_curtains_loop.webm";
const THEATRE_IMG1 = "/frontassets/theatre_img1.jpg";
const ENTER_THEATRE_MOV = "/frontassets/enter_theatre.mov";
const ENTER_THEATRE_WEBM = "/frontassets/enter_theatre.webm";

// Mathematical helper functions
function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return clamp((value - inMin) / (inMax - inMin), 0, 1) * (outMax - outMin) + outMin;
}

// Hook to detect orientation (Portrait vs. Landscape)
function subscribeToOrientation(cb: () => void) {
  const media = window.matchMedia("(orientation: portrait)");
  media.addEventListener("change", cb);
  return () => media.removeEventListener("change", cb);
}

function useIsVertical(): boolean {
  return useSyncExternalStore(
    subscribeToOrientation,
    () => window.matchMedia("(orientation: portrait)").matches
  );
}

// Stage mapping: card ID → IntentContext stage value
const CARD_STAGE_MAP = {
  A: "previous",
  B: "upcoming",
  C: "midseason",
} as const;

// Track mapping: option panel key → IntentContext track value
const OPTION_TRACK_MAP = {
  profit: "profit",
  audience: "seats",
  brand: "brand",
} as const;

export function FringeLandingPage() {
  const navigate = useNavigate();
  const isVertical = useIsVertical();
  const { setState } = useIntent();

  // Card interaction state
  const [expandedCard, setExpandedCard] = useState<'A' | 'B' | 'C' | null>(null);
  const [hoveredCard, setHoveredCard] = useState<'A' | 'B' | 'C' | null>(null);

  // Exit sequence state
  const [exitPhase, setExitPhase] = useState<
    'idle' | 'div-exit' | 'curtains-hide' | 'video-playing' | 'panels-in' | 'panels-out' | 'zoom-fade' | 'done'
  >('idle');

  // Option Hover state
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);

  // Refs for scrubbing and virtual scroll
  const containerRef = useRef<HTMLDivElement>(null);
  const loopVideoRef = useRef<HTMLVideoElement>(null);
  const enterVideoRef = useRef<HTMLVideoElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Direct-DOM refs for RAF-driven scroll updates (no setState per frame)
  const curtainLeftRef = useRef<HTMLImageElement>(null);
  const curtainRightRef = useRef<HTMLImageElement>(null);
  const contentDivRef = useRef<HTMLDivElement>(null);
  const curtainProgressRef = useRef(0);
  const rafIdRef = useRef(0);

  // RAF-gated scroll handler — direct DOM updates, no setState per frame
  const onScroll = useCallback(() => {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;

      const container = containerRef.current;
      if (!container) return;

      const progress = clamp(
        window.scrollY / (container.scrollHeight - window.innerHeight),
        0,
        1
      );

      // Phase 1: Curtain Open (0.00 – 0.30)
      const curtainProg = mapRange(progress, 0, 0.30, 0, 1);
      curtainProgressRef.current = curtainProg;

      // Curtain images — direct DOM transform
      if (curtainLeftRef.current) {
        curtainLeftRef.current.style.transform = `translateX(${lerp(0, -100, curtainProg)}%)`;
      }
      if (curtainRightRef.current) {
        curtainRightRef.current.style.transform = `translateX(${lerp(0, 100, curtainProg)}%)`;
      }

      // Loop video visibility — direct DOM opacity
      if (loopVideoRef.current) {
        loopVideoRef.current.style.opacity = curtainProg === 0 ? "1" : "0";
      }

      // Phase 3: Div Resize (0.35 – 0.55) — direct DOM style
      const divScale = mapRange(progress, 0.35, 0.55, 0, 1);
      if (contentDivRef.current) {
        contentDivRef.current.style.width = `${lerp(25, 80, divScale)}%`;
        contentDivRef.current.style.height = `${lerp(25, 80, divScale)}%`;
      }

      // Phase 4: Div Internal Scroll (0.55 – 0.85)
      const divScrollProgress = mapRange(progress, 0.55, 0.85, 0, 1);
      if (contentRef.current) {
        const maxScroll = contentRef.current.scrollHeight - contentRef.current.clientHeight;
        contentRef.current.scrollTop = divScrollProgress * maxScroll;
      }

      // SMT logo overlay visibility — direct DOM
      const logoOverlay = document.getElementById("smt-logo-overlay");
      if (logoOverlay) {
        const show = curtainProg === 0;
        logoOverlay.style.opacity = show ? "1" : "0";
        logoOverlay.style.visibility = show ? "visible" : "hidden";
        logoOverlay.style.pointerEvents = show ? "auto" : "none";
      }
    });
  }, []);

  useEffect(() => {
    if (exitPhase !== "idle") return;

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, [exitPhase, onScroll]);

  // Handle enter theatre video metadata loaded
  const handleEnterVideoLoaded = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    video.currentTime = 0;
    video.pause();
  };

  // When a card is selected, set stage on IntentContext, expand it, then auto-trigger exit sequence
  const handleCardClick = (cardId: 'A' | 'B' | 'C') => {
    const stage = CARD_STAGE_MAP[cardId];
    setState(prev => ({ ...prev, stage }));
    setExpandedCard(cardId);
  };

  // Exit Animation Sequence — reordered: div drops → curtains hide → video plays
  const triggerExitSequence = () => {
    // Step 1: Content div slides down off screen (600ms)
    setExitPhase("div-exit");

    // Step 2: After div is fully off-screen, hide curtain overlays
    setTimeout(() => {
      setExitPhase("curtains-hide");
    }, 700);

    // Step 3: After curtains gone, play enter_theatre video
    setTimeout(() => {
      if (enterVideoRef.current) {
        setExitPhase("video-playing");
        enterVideoRef.current.currentTime = 0;
        enterVideoRef.current.play().catch(() => {
          setExitPhase("panels-in");
        });
      } else {
        setExitPhase("panels-in");
      }
    }, 1000);
  };

  // Auto-trigger the exit sequence shortly after a card is expanded
  useEffect(() => {
    if (expandedCard === null) return;
    const timer = setTimeout(() => {
      triggerExitSequence();
    }, 600);
    return () => clearTimeout(timer);
  }, [expandedCard]);

  // Step 4: Enter theatre video plays to completion and triggers Option Panels
  const handleEnterVideoEnded = () => {
    if (exitPhase === "video-playing") {
      setExitPhase("panels-in");
    }
  };

  // Step 5: Option selected — set track on IntentContext, then navigate
  const handleOptionClick = (optionKey: string) => {
    const track = OPTION_TRACK_MAP[optionKey as keyof typeof OPTION_TRACK_MAP];
    if (track) {
      setState(prev => ({ ...prev, track }));
    }

    setExitPhase("panels-out");

    // Fade out and zoom background video and theatre background image simultaneously (1.2s)
    setTimeout(() => {
      setExitPhase("done");
      navigate("/fringe/getstarted");
    }, 1200);
  };

  // Auto-scroll logic over 6 seconds to bottom of page
  const scrollToBottom = () => {
    const duration = 6000; // 6 seconds in ms
    const start = window.scrollY;
    const end = document.documentElement.scrollHeight - window.innerHeight;
    const startTime = performance.now();

    const animateScroll = (now: number) => {
      const elapsed = now - startTime;
      const progress = clamp(elapsed / duration, 0, 1);

      // Ease-in-out cubic function for premium and state of the art smoothness
      const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const easedProgress = easeInOutCubic(progress);

      window.scrollTo(0, lerp(start, end, easedProgress));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  // Inline CSS Styles tailored to layers
  const curtainAreaStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: isVertical ? "calc(100vw * 9 / 16)" : "100%",
    background: "#0a0608",
    overflow: "hidden",
  };

  const getExpandedStyle = (cardId: 'A' | 'B' | 'C'): React.CSSProperties => {
    const isActive = expandedCard === cardId;
    const origins = { A: "16% 50%", B: "50% 50%", C: "84% 50%" };
    return {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      borderRadius: isActive ? 0 : "clamp(10px, 1.5vw, 18px)",
      background: "rgba(18, 18, 28, 0.92)",
      zIndex: 100,
      opacity: isActive ? 1 : 0,
      transform: isActive ? "scale(1)" : "scale(0)",
      transformOrigin: origins[cardId],
      pointerEvents: "none",
      transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease, border-radius 0.4s ease",
      boxSizing: "border-box",
    };
  };

  // Visible option panels check
  const showPanels = exitPhase === "panels-in" || exitPhase === "panels-out";

  return (
    <div
      ref={containerRef}
      style={{
        height: "800dvh",
        position: "relative",
        background: "#0a0608",
      }}
    >
      {/* Sticky Viewport */}
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100dvh",
          width: "100vw",
          overflow: "hidden",
          background: "#0a0608",
        }}
      >
        {/* Landscape/Aspect Ratio Curtain Area */}
        <div style={curtainAreaStyle}>

          {/* Layer 1: Theatre Background Image */}
          <img
            src={THEATRE_IMG1}
            alt="Theatre Backdrop"
            loading="eager"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
              transform: exitPhase === "panels-out" ? "scale(2)" : "scale(1)",
              opacity: exitPhase === "panels-out" ? 0 : 1,
              transition: "transform 1.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 1.2s ease",
            }}
          />

          {/* Layer 2: Enter Theatre Video */}
          <video
            ref={enterVideoRef}
            preload="metadata"
            muted
            playsInline
            onLoadedData={handleEnterVideoLoaded}
            onEnded={handleEnterVideoEnded}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 2,
              pointerEvents: "none",
              opacity: (exitPhase === "video-playing" || exitPhase === "panels-in") ? 1 : 0,
              transform: exitPhase === "panels-out" ? "scale(2)" : "scale(1)",
              transition: exitPhase === "panels-out"
                ? "transform 1.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 1.2s ease"
                : "opacity 0.3s ease",
            }}
          >
            <source src={ENTER_THEATRE_WEBM} type="video/webm" />
            <source src={ENTER_THEATRE_MOV} type='video/quicktime; codecs="hvc1"' />
          </video>

          {/* Layer 3: Content Div — contentDivRef for RAF sizing, contentRef for scrollTop */}
          <div
            ref={(el) => { contentDivRef.current = el; contentRef.current = el; }}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "25%",
              height: "25%",
              backgroundColor: "rgba(18, 18, 28, 0.92)",
              backgroundImage: "url('/frontassets/noise.png')",
              backgroundRepeat: "repeat",
              backgroundSize: "200%",
              backgroundAttachment: "local",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 8px 60px rgba(0,0,0,0.6), 0 0 20px 5px rgba(42, 74, 158, 0.25)",
              zIndex: 10,
              transform: exitPhase !== "idle" && exitPhase !== "done"
                ? "translate(-50%, 100dvh)"
                : "translate(-50%, -50%)",
              transition: exitPhase !== "idle" && exitPhase !== "done"
                ? "transform 0.6s cubic-bezier(0.55, 0, 1, 0.45)"
                : "none",
            }}
          >
            {/* Scroll Wrapper inside the Content Div */}
            <div
              style={{
                width: "100%",
                height: "200%",
                position: "relative",
              }}
            >
              {/* Section 1 — Hero */}
              <div
                style={{
                  width: "100%",
                  height: "50%", // Fits one full div-height
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "clamp(14px, 2vw, 18px)",
                    fontWeight: 300,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#9695a4",
                    marginBottom: "8px",
                  }}
                >
                  welcome to
                </span>
                <h2
                  style={{
                    fontSize: "clamp(28px, 5vw, 48px)",
                    fontWeight: 700,
                    color: "#fafafc",
                    letterSpacing: "-0.02em",
                    margin: 0,
                  }}
                >
                  SellMoreTickets
                  <span style={{ color: "#b87800" }}>.live</span>
                </h2>
              </div>

              {/* Section 2 — Three Dark Panels */}
              <div style={{ width: "100%", height: "50%", position: "relative", boxSizing: "border-box", overflow: "hidden", display: "flex", flexDirection: "column" }}>

                {/* ── Heading block ── */}
                <div style={{ textAlign: "center", padding: "clamp(10px, 4%, 32px) clamp(12px, 5%, 40px) clamp(6px, 2%, 16px)", flexShrink: 0 }}>
                  <h3 style={{
                    fontSize: "clamp(14px, 2.2vw, 28px)", fontWeight: 900, color: "#fafafc",
                    margin: "0 0 clamp(2px, 0.6%, 8px) 0", letterSpacing: "-0.01em",
                    textTransform: "uppercase", lineHeight: 1.15,
                    textShadow: "0 0 20px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,1)",
                  }}>
                    WHERE ARE YOU IN YOUR FRINGE JOURNEY?
                  </h3>
                  <p style={{ fontSize: "clamp(9px, 1.1vw, 15px)", color: "#9695a4", margin: 0, fontWeight: 400, fontStyle: "italic" }}>
                    This shapes everything — your dashboard, your insights, your first action.
                  </p>
                </div>

                {/* ── Card row ── */}
                <div style={{
                  flex: 1, display: "flex", alignItems: "stretch",
                  gap: "clamp(8px, 2%, 20px)",
                  padding: "0 clamp(10px, 4%, 32px) clamp(10px, 3%, 20px)",
                  boxSizing: "border-box", minHeight: 0,
                  opacity: expandedCard === null ? 1 : 0,
                  pointerEvents: expandedCard === null ? "auto" : "none",
                  transition: "opacity 0.3s ease",
                }}>
                  {([
                    {
                      id: "A" as const, title: "Analyse my previous show(s)", icon: "/frontassets/priorshow_icon.png",
                      subtitle: ": Season's over — show me what happened",
                      desc: "- Your data is sitting there doing nothing. We'll turn it into a plan for next time.",
                      btn: "SELECT ANALYSE PREVIOUS", glow: "#b87800", border: "rgba(184, 120, 0, 0.5)",
                      glowShadow: "0 0 20px rgba(184, 120, 0, 0.4), 0 0 60px rgba(184, 120, 0, 0.15)",
                      imgGlow: "drop-shadow(0 0 18px rgba(184, 120, 0, 0.6))", bounceClass: "btn-bounce-a",
                    },
                    {
                      id: "B" as const, title: "I have a show coming up", icon: "/frontassets/upcoming_icon.png",
                      subtitle: ": Registered for an upcoming festival season",
                      desc: "- Get set up now so your live dashboard activates the moment tickets go on sale.",
                      btn: "SELECT SHOW COMING UP", glow: "#3d8bd4", border: "rgba(61, 139, 212, 0.5)",
                      glowShadow: "0 0 20px rgba(61, 139, 212, 0.4), 0 0 60px rgba(61, 139, 212, 0.15)",
                      imgGlow: "drop-shadow(0 0 18px rgba(61, 139, 212, 0.6))", bounceClass: "btn-bounce-b",
                    },
                    {
                      id: "C" as const, title: "I'm mid-season right now", icon: "/frontassets/midseason_icon.png",
                      subtitle: ": Currently performing at a festival",
                      desc: "- Connect now and your live dashboard starts tracking immediately. Every ticket, every show, right now.",
                      btn: "SELECT MID-SEASON", glow: "#9333ea", border: "rgba(147, 51, 234, 0.5)",
                      glowShadow: "0 0 20px rgba(147, 51, 234, 0.4), 0 0 60px rgba(147, 51, 234, 0.15)",
                      imgGlow: "drop-shadow(0 0 18px rgba(147, 51, 234, 0.6))", bounceClass: "btn-bounce-c",
                    },
                  ]).map((card) => (
                    <div
                      key={card.id}
                      onClick={() => handleCardClick(card.id)}
                      onMouseEnter={() => setHoveredCard(card.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      style={{
                        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                        background: "rgba(18, 18, 28, 0.92)",
                        backdropFilter: "blur(16px)",
                        border: `1.5px solid ${card.border}`,
                        borderRadius: "14px",
                        padding: "clamp(12px, 2.5%, 24px)",
                        boxSizing: "border-box", cursor: "pointer", overflow: "hidden",
                        boxShadow: hoveredCard === card.id ? card.glowShadow : "0 4px 30px rgba(0,0,0,0.5)",
                        transition: "box-shadow 0.3s ease",
                      }}
                    >
                      {/* Title */}
                      <h4 style={{
                        fontSize: "clamp(12px, 1.6vw, 22px)", fontWeight: 800, color: "#fafafc",
                        textAlign: "center", margin: "0 0 clamp(6px, 1.5%, 14px) 0",
                        letterSpacing: "0.02em", lineHeight: 1.2,
                      }}>
                        {card.title}
                      </h4>

                      {/* Icon image with glow */}
                      <div style={{
                        width: "clamp(60px, 55%, 180px)", aspectRatio: "1",
                        borderRadius: "12px", overflow: "hidden",
                        border: `1px solid ${card.border}`,
                        boxShadow: `inset 0 0 30px rgba(0,0,0,0.3), ${card.glowShadow}`,
                        marginBottom: "clamp(6px, 1.5%, 14px)",
                        flexShrink: 0,
                      }}>
                        <img src={card.icon} alt={card.title} style={{
                          width: "100%", height: "100%", objectFit: "cover",
                          filter: card.imgGlow,
                        }} />
                      </div>

                      {/* Subtitle */}
                      <p style={{
                        fontSize: "clamp(8px, 1vw, 14px)", fontWeight: 700, color: "#fafafc",
                        textAlign: "center", margin: "0 0 clamp(4px, 1%, 8px) 0", lineHeight: 1.35,
                      }}>
                        {card.subtitle}
                      </p>

                      {/* Description — vertically centered */}
                      <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "clamp(2px, 0.5%, 6px) 0" }}>
                        <p style={{
                          fontSize: "clamp(7px, 0.85vw, 13px)", color: "#b0b0c0",
                          textAlign: "center", lineHeight: 2.0, margin: 0,
                        }}>
                          {card.desc}
                        </p>
                      </div>

                      {/* Button */}
                      <button className={card.bounceClass} style={{
                        width: "100%", padding: "clamp(8px, 1.4%, 14px) 0",
                        background: "transparent",
                        border: `1.5px solid ${card.glow}`,
                        borderRadius: "6px",
                        color: card.glow, fontWeight: 700,
                        fontSize: "clamp(7px, 0.8vw, 12px)",
                        textTransform: "uppercase", letterSpacing: "0.1em",
                        cursor: "pointer",
                        boxShadow: hoveredCard === card.id ? `0 0 12px ${card.glow}40` : "none",
                        transition: "box-shadow 0.3s ease",
                      }}>
                        {card.btn}
                      </button>
                    </div>
                  ))}
                </div>

                {/* ── Expanded card overlays — always mounted, scale from card origin ── */}
                {(["A", "B", "C"] as const).map((cardId) => (
                  <div key={cardId} style={getExpandedStyle(cardId)} />
                ))}
              </div>
            </div>
          </div>

          {/* Layer 4: Curtain Images — primary scroll-driven curtain open */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              visibility: exitPhase === "idle" || exitPhase === "div-exit" ? "visible" : "hidden",
              zIndex: 27,
            }}
          >
            {/* Left Curtain Image Fallback */}
            <img
              ref={curtainLeftRef}
              src={FRONT_CURTAIN_LEFT}
              alt="Left Curtain"
              loading="eager"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "50%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "right center",
                transform: "translateX(0%)",
                transition: "transform 0.05s linear",
              }}
            />
            {/* Right Curtain Image Fallback */}
            <img
              ref={curtainRightRef}
              src={FRONT_CURTAIN_RIGHT}
              alt="Right Curtain"
              loading="eager"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "50%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "left center",
                transform: "translateX(0%)",
                transition: "transform 0.05s linear",
              }}
            />
          </div>

          {/* Layer 6: Front Curtains Loop Video — Idle Sway */}
          <video
            ref={loopVideoRef}
            preload="auto"
            muted
            playsInline
            loop
            autoPlay
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 26,
              pointerEvents: "none",
              opacity: (exitPhase === "idle" || exitPhase === "div-exit") ? 1 : 0,
              visibility: (exitPhase === "idle" || exitPhase === "div-exit") ? "visible" : "hidden",
              transition: "opacity 0.4s ease",
            }}
          >
            <source src={FRONT_CURTAINS_LOOP} type="video/webm" />
            <source src="/frontassets/front_curtains_loop.mov" type='video/quicktime; codecs="hvc1"' />
          </video>

          {/* Layer 9: Top Layer SMT Logo overlay (z-index: 28) */}
          <div
            id="smt-logo-overlay"
            style={{
              position: "absolute",
              left: "50%",
              top: "30%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              textAlign: "center",
              zIndex: 28,
              opacity: exitPhase === 'idle' ? 1 : 0,
              visibility: exitPhase === 'idle' ? 'visible' : 'hidden',
              pointerEvents: exitPhase === 'idle' ? 'auto' : 'none',
              transition: "opacity 0.4s ease, visibility 0.4s ease",
            }}
          >
            {/* SMT Logo */}
            <img
              src="/frontassets/SMT.png"
              alt="SMT Logo"
              loading="eager"
              style={{
                width: "30%",
                maxWidth: isVertical ? "200px" : "320px",
                height: "auto",
              }}
            />

            {/* Tag Line */}
            <div
              style={{
                color: "#fafafc",
                fontSize: "clamp(12px, 1.8vw, 18px)",
                fontWeight: 500,
                marginTop: "16px",
                padding: "0 20px",
                textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                maxWidth: "80%",
                lineHeight: "1.4",
              }}
            >
              Your Companion and Coach in Mastering Fringe Show Ticket Sales
            </div>

            {/* Down Arrow - continuous pulsation */}
            <img
              src="/frontassets/downarrow.png"
              alt="Scroll Down Arrow"
              loading="eager"
              onClick={scrollToBottom}
              className="animate-pulse-opacity"
              style={{
                width: "48px",
                height: "auto",
                marginTop: "24px",
                cursor: "pointer",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            />
          </div>

          {/* Layer 8: Option Panels — "Choose Your Path" */}
          {exitPhase !== "idle" && (() => {
            const panelData = [
              {
                key: "profit", title: "MAXIMIZE PROFIT", icon: "/frontassets/profit_icon.png",
                desc: "Focus on premium ticket pricing, exclusive packages, and high-margin sales strategies to secure the highest financial return per event.",
                btn: "SELECT MAX PROFIT", glow: "#b87800", border: "rgba(184, 120, 0, 0.5)",
                glowShadow: "0 0 20px rgba(184, 120, 0, 0.4), 0 0 60px rgba(184, 120, 0, 0.15)",
                imgGlow: "drop-shadow(0 0 18px rgba(184, 120, 0, 0.6))",
                entryTransform: "translateX(calc(-100% - 50vw))", delay: "0s",
              },
              {
                key: "audience", title: "FILL ALL SHOWS", icon: "/frontassets/audience_icon.png",
                desc: "Prioritize volume, dynamic pricing, and broad promotional reach to ensure every seat is sold and venues are at capacity.",
                btn: "SELECT FILL SHOWS", glow: "#3d8bd4", border: "rgba(61, 139, 212, 0.5)",
                glowShadow: "0 0 20px rgba(61, 139, 212, 0.4), 0 0 60px rgba(61, 139, 212, 0.15)",
                imgGlow: "drop-shadow(0 0 18px rgba(61, 139, 212, 0.6))",
                entryTransform: "translate(-50%, calc(-100% - 20vh))", delay: "0.1s",
              },
              {
                key: "brand", title: "BUILD BRAND REPUTATION", icon: "/frontassets/brand_icon.png",
                desc: "Cultivate critical acclaim, foster loyal fan communities, and secure long-term industry recognition through curated experiences.",
                btn: "SELECT BUILD REPUTATION", glow: "#9333ea", border: "rgba(147, 51, 234, 0.5)",
                glowShadow: "0 0 20px rgba(147, 51, 234, 0.4), 0 0 60px rgba(147, 51, 234, 0.15)",
                imgGlow: "drop-shadow(0 0 18px rgba(147, 51, 234, 0.6))",
                entryTransform: "translateX(calc(100% + 50vw))", delay: "0.2s",
              },
            ];
            const positions = [
              { right: "calc(50% + 12.5% - 3.5px + 20px)" },
              { left: "50%" },
              { left: "calc(50% + 12.5% - 3.5px + 20px)" },
            ];
            const getTransform = (i: number) => {
              if (exitPhase === "panels-in") return i === 1 ? "translate(-50%, 0)" : "translateX(0)";
              return panelData[i].entryTransform;
            };
            return (
              <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 50, pointerEvents: showPanels ? "auto" : "none" }}>
                {/* Heading */}
                <h2 style={{
                  position: "absolute", top: "2%", left: "50%", transform: "translateX(-50%)", width: "90%",
                  textAlign: "center", fontSize: "clamp(16px, 2.5vw, 32px)", fontWeight: 800,
                  color: "#fafafc", letterSpacing: "0.02em", textTransform: "uppercase", margin: 0,
                  textShadow: "0 0 20px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,1)",
                  opacity: exitPhase === "panels-in" ? 1 : 0,
                  transition: "opacity 0.6s ease 0.3s",
                }}>
                  CHOOSE YOUR PATH: DEFINE YOUR PLATFORM GOALS.
                </h2>

                {panelData.map((p, i) => (
                  <div
                    key={p.key}
                    style={{
                      position: "absolute",
                      ...positions[i],
                      top: "12%",
                      width: "calc(25% - 7px)",
                      minHeight: "82%",
                      background: "rgba(18, 18, 28, 0.92)",
                      backdropFilter: "blur(16px)",
                      border: `1.5px solid ${p.border}`,
                      borderRadius: "14px",
                      padding: "clamp(16px, 2vw, 28px)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      boxSizing: "border-box",
                      boxShadow: hoveredOption === p.key ? p.glowShadow : "0 4px 30px rgba(0,0,0,0.5)",
                      transform: getTransform(i),
                      transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease",
                      transitionDelay: exitPhase === "panels-in" ? p.delay : "0s",
                      cursor: "pointer",
                    }}
                    onClick={() => handleOptionClick(p.key)}
                    onMouseEnter={() => setHoveredOption(p.key)}
                    onMouseLeave={() => setHoveredOption(null)}
                  >
                    {/* Title */}
                    <h3 style={{
                      fontSize: "clamp(12px, 1.6vw, 22px)", fontWeight: 800, color: "#fafafc",
                      textAlign: "center", margin: "0 0 clamp(8px, 2%, 16px) 0",
                      letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.2,
                    }}>
                      {p.title}
                    </h3>

                    {/* Icon image with glow */}
                    <div style={{
                      width: "clamp(80px, 60%, 200px)", aspectRatio: "1",
                      borderRadius: "12px", overflow: "hidden",
                      border: `1px solid ${p.border}`,
                      boxShadow: `inset 0 0 30px rgba(0,0,0,0.3), ${p.glowShadow}`,
                      marginBottom: "clamp(8px, 2%, 16px)",
                      flexShrink: 0,
                    }}>
                      <img
                        src={p.icon}
                        alt={p.title}
                        style={{
                          width: "100%", height: "100%", objectFit: "cover",
                          filter: p.imgGlow,
                        }}
                      />
                    </div>

                    {/* Description — vertically centered in remaining space */}
                    <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "clamp(4px, 1%, 8px) 0" }}>
                      <p style={{
                        fontSize: "clamp(9px, 1vw, 14px)", color: "#b0b0c0",
                        textAlign: "center", lineHeight: 2.0, margin: 0,
                      }}>
                        {p.desc}
                      </p>
                    </div>

                    {/* Button */}
                    <button style={{
                      width: "100%", padding: "clamp(8px, 1.4%, 14px) 0",
                      background: "transparent",
                      border: `1.5px solid ${p.glow}`,
                      borderRadius: "6px",
                      color: p.glow, fontWeight: 700,
                      fontSize: "clamp(8px, 0.85vw, 12px)",
                      textTransform: "uppercase", letterSpacing: "0.1em",
                      cursor: "pointer",
                      boxShadow: hoveredOption === p.key ? `0 0 12px ${p.glow}40` : "none",
                      transition: "box-shadow 0.3s ease",
                    }}>
                      {p.btn}
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}

        </div>

        {/* Layer 7: Portrait-Mode Bottom Overlay */}
        {isVertical && (
          <div
            style={{
              position: "absolute",
              top: "calc(100vw * 9 / 16)",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              pointerEvents: "none",
            }}
          >
            {/* 7a: Gradient fade */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "50dvh",
                background: "linear-gradient(to bottom, transparent 0%, #000000 10px, #000000 100%)",
              }}
            />

            {/* 7b: Rotate phone icon */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                viewBox="0 0 100 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-phone-rotate"
                style={{
                  width: "40vw",
                  maxWidth: "180px",
                  opacity: 0.3,
                  color: "#ffffff",
                }}
              >
                {/* Phone body */}
                <rect x="30" y="15" width="40" height="70" rx="6" />
                {/* Screen */}
                <rect x="34" y="22" width="32" height="52" rx="2" opacity="0.3" />
                {/* Home button dot */}
                <circle cx="50" cy="80" r="2" />
                {/* Rotation arrow - curves around the phone */}
                <path d="M 18 50 A 35 35 0 0 1 50 12" />
                <path d="M 45 8 L 50 12 L 44 16" />
              </svg>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
